import { useState, useEffect, useRef } from 'react';
import { useSongs } from '@/context/SongContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { db } from '@/lib/db';
import { formatDate, formatDuration, FRAGMENT_COLORS } from '@/lib/utils';
import ColorPicker from '@/components/ColorPicker';
import AudioTrimmer from '@/components/tools/AudioTrimmer';
import MoveFragmentDialog from '@/components/MoveFragmentDialog';
import {
    Mic, FileText, Music, Guitar, Volume2, Radio, ExternalLink, Disc3, Waves,
    Trash2, Play, Pause, Pencil, Check, X, Scissors, ArrowRightLeft
} from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Fragment types that can be fully edited
const EDITABLE_TYPES = ['text', 'metadata', 'instrument', 'link', 'inspiration', 'drone'];

const fragmentConfig = {
    audio: {
        icon: Mic,
        color: 'fragment-audio',
        bgClass: 'bg-fragment-audio-subtle',
        label: 'Recording'
    },
    text: {
        icon: FileText,
        color: 'fragment-text',
        bgClass: 'bg-fragment-text-subtle',
        label: 'Note'
    },
    metadata: {
        icon: Music,
        color: 'fragment-metadata',
        bgClass: 'bg-fragment-metadata-subtle',
        label: 'Song Info'
    },
    instrument: {
        icon: Guitar,
        color: 'fragment-instrument',
        bgClass: 'bg-fragment-instrument-subtle',
        label: 'Instrument Notes'
    },
    tone: {
        icon: Volume2,
        color: 'fragment-tone',
        bgClass: 'bg-fragment-tone-subtle',
        label: 'Reference Pitch'
    },
    tuning: {
        icon: Radio,
        color: 'fragment-tuning',
        bgClass: 'bg-fragment-tuning-subtle',
        label: 'Guitar Tuning'
    },
    link: {
        icon: ExternalLink,
        color: 'fragment-link',
        bgClass: 'bg-fragment-link-subtle',
        label: 'External Link'
    },
    inspiration: {
        icon: Disc3,
        color: 'fragment-inspiration',
        bgClass: 'bg-fragment-inspiration-subtle',
        label: 'Reference'
    },
    drone: {
        icon: Waves,
        color: 'fragment-drone',
        bgClass: 'bg-fragment-drone-subtle',
        label: 'Drone'
    }
};

export default function FragmentCard({ fragment, songId }) {
    const { deleteFragment, updateFragment } = useSongs();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [moveOpen, setMoveOpen] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [audioUrl, setAudioUrl] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [showTrimmer, setShowTrimmer] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const audioRef = useRef(null);

    const config = fragmentConfig[fragment.type] || fragmentConfig.text;
    const Icon = config.icon;
    const colorClass = config.color;
    const canEdit = EDITABLE_TYPES.includes(fragment.type) || fragment.label !== undefined;

    // Get custom color override background
    const getCustomBgStyle = () => {
        if (!fragment.colorOverride) return {};
        const color = FRAGMENT_COLORS.find(c => c.id === fragment.colorOverride);
        if (color?.cssVar) {
            return { backgroundColor: `hsl(var(${color.cssVar}))` };
        }
        return {};
    };

    const hasCustomColor = !!fragment.colorOverride;

    // Load audio blob if it's an audio fragment
    useEffect(() => {
        if (fragment.type === 'audio' && fragment.blobId) {
            db.getAudioBlob(fragment.blobId).then(blob => {
                if (blob) {
                    const url = URL.createObjectURL(blob);
                    setAudioUrl(url);
                    setAudioBlob(blob);
                }
            });
        }
        return () => {
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fragment.blobId, fragment.type]);

    // Audio playback handling
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [audioUrl]);

    const togglePlayback = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleEdit = () => {
        // Initialize edit data based on fragment type
        const data = { label: fragment.label || '' };
        
        switch (fragment.type) {
            case 'text':
                data.content = fragment.content || '';
                break;
            case 'metadata':
                data.bpm = fragment.bpm || 120;
                data.timeSignature = fragment.timeSignature || '4/4';
                break;
            case 'instrument':
                data.instrument = fragment.instrument || '';
                data.tuning = fragment.tuning || '';
                data.capo = fragment.capo || 0;
                data.chords = fragment.chords || '';
                data.notes = fragment.notes || '';
                break;
            case 'link':
                data.url = fragment.url || '';
                data.description = fragment.description || '';
                break;
            case 'inspiration':
                data.url = fragment.url || '';
                data.note = fragment.note || '';
                break;
            case 'drone':
                data.note = fragment.note || 'A2';
                data.frequency = fragment.frequency || 110;
                break;
            default:
                break;
        }
        
        setEditData(data);
        setEditing(true);
    };

    const handleSave = () => {
        const updates = { label: editData.label };
        
        switch (fragment.type) {
            case 'text':
                updates.content = editData.content;
                break;
            case 'metadata':
                updates.bpm = parseInt(editData.bpm) || 120;
                updates.timeSignature = editData.timeSignature;
                break;
            case 'instrument':
                updates.instrument = editData.instrument;
                updates.tuning = editData.tuning;
                updates.capo = parseInt(editData.capo) || 0;
                updates.chords = editData.chords;
                updates.notes = editData.notes;
                break;
            case 'link':
                updates.url = editData.url;
                updates.description = editData.description;
                break;
            case 'inspiration':
                updates.url = editData.url;
                updates.note = editData.note;
                break;
            case 'drone':
                updates.note = editData.note;
                updates.frequency = parseFloat(editData.frequency) || 110;
                break;
            default:
                break;
        }
        
        updateFragment(songId, fragment.id, updates);
        setEditing(false);
    };

    const handleCancelEdit = () => {
        setEditing(false);
        setEditData({});
    };

    const handleDelete = () => {
        deleteFragment(songId, fragment.id);
        setDeleteOpen(false);
    };

    const handleColorChange = (colorId) => {
        updateFragment(songId, fragment.id, { colorOverride: colorId });
    };

    // Handle audio trim save
    const handleTrimSave = async (newBlob, newDuration) => {
        try {
            // Save new blob to IndexedDB
            const newBlobId = `audio-${Date.now()}-${Math.random().toString(36).slice(2)}`;
            await db.saveAudioBlob(newBlobId, newBlob);
            
            // Delete old blob
            if (fragment.blobId) {
                await db.deleteAudioBlob(fragment.blobId);
            }
            
            // Update fragment
            updateFragment(songId, fragment.id, {
                blobId: newBlobId,
                duration: newDuration,
                mimeType: 'audio/wav'
            });
            
            // Update local state
            const url = URL.createObjectURL(newBlob);
            if (audioUrl) URL.revokeObjectURL(audioUrl);
            setAudioUrl(url);
            setAudioBlob(newBlob);
            setShowTrimmer(false);
        } catch (error) {
            console.error('Error saving trimmed audio:', error);
        }
    };

    const renderContent = () => {
        switch (fragment.type) {
            case 'audio':
                if (showTrimmer && audioBlob) {
                    return (
                        <AudioTrimmer 
                            audioBlob={audioBlob}
                            onSave={handleTrimSave}
                            onCancel={() => setShowTrimmer(false)}
                        />
                    );
                }
                return (
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={togglePlayback}
                                disabled={!audioUrl}
                                className={`shrink-0 h-11 w-11 rounded-full hover:scale-105 active:scale-95 transition-transform ${
                                    isPlaying ? 'border-primary bg-primary/5' : ''
                                }`}
                            >
                                {isPlaying ? (
                                    <Pause className="w-5 h-5" />
                                ) : (
                                    <Play className="w-5 h-5 ml-0.5" />
                                )}
                            </Button>
                            <div className="flex-1 min-w-0">
                                <div className="h-2 bg-muted rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all duration-100"
                                        style={{
                                            width: `${fragment.duration ? (currentTime / fragment.duration) * 100 : 0}%`,
                                            backgroundColor: `hsl(var(--${colorClass}))`
                                        }}
                                    />
                                </div>
                                <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
                                    <span>{formatDuration(currentTime)}</span>
                                    <span>{formatDuration(fragment.duration)}</span>
                                </div>
                            </div>
                            {audioUrl && <audio ref={audioRef} src={audioUrl} />}
                        </div>
                        {/* Trim button */}
                        {audioBlob && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowTrimmer(true)}
                                className="h-7 text-xs text-muted-foreground hover:text-foreground"
                            >
                                <Scissors className="w-3 h-3 mr-1.5" />
                                Trim
                            </Button>
                        )}
                    </div>
                );

            case 'text':
                return editing ? (
                    <Textarea
                        value={editData.content || ''}
                        onChange={(e) => setEditData({ ...editData, content: e.target.value })}
                        placeholder="Write your lyrics, notes, or thoughts..."
                        className="min-h-[100px] resize-none"
                        autoFocus
                    />
                ) : (
                    <p className="text-foreground whitespace-pre-wrap leading-relaxed">
                        {fragment.content || <span className="text-muted-foreground italic">No content</span>}
                    </p>
                );

            case 'metadata':
                return editing ? (
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">BPM</Label>
                            <Input
                                type="number"
                                value={editData.bpm || 120}
                                onChange={(e) => setEditData({ ...editData, bpm: e.target.value })}
                                min={40}
                                max={240}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Time Signature</Label>
                            <Input
                                value={editData.timeSignature || '4/4'}
                                onChange={(e) => setEditData({ ...editData, timeSignature: e.target.value })}
                                placeholder="4/4"
                                className="mt-1"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-3 gap-4 text-sm">
                        {fragment.bpm && (
                            <div className="p-3 rounded-lg bg-background/50">
                                <span className="text-muted-foreground block text-xs">BPM</span>
                                <span className="text-foreground font-semibold text-xl">{fragment.bpm}</span>
                            </div>
                        )}
                        {fragment.timeSignature && (
                            <div className="p-3 rounded-lg bg-background/50">
                                <span className="text-muted-foreground block text-xs">Time</span>
                                <span className="text-foreground font-medium">{fragment.timeSignature}</span>
                            </div>
                        )}
                        {fragment.key && (
                            <div className="p-3 rounded-lg bg-background/50">
                                <span className="text-muted-foreground block text-xs">Key</span>
                                <span className="text-foreground font-medium">{fragment.key}</span>
                            </div>
                        )}
                    </div>
                );

            case 'instrument':
                return editing ? (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Instrument</Label>
                                <Input
                                    value={editData.instrument || ''}
                                    onChange={(e) => setEditData({ ...editData, instrument: e.target.value })}
                                    placeholder="Guitar, Piano..."
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Tuning</Label>
                                <Input
                                    value={editData.tuning || ''}
                                    onChange={(e) => setEditData({ ...editData, tuning: e.target.value })}
                                    placeholder="Standard, Drop D..."
                                    className="mt-1"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Capo Position</Label>
                            <Input
                                type="number"
                                value={editData.capo || 0}
                                onChange={(e) => setEditData({ ...editData, capo: e.target.value })}
                                min={0}
                                max={12}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Chords</Label>
                            <Input
                                value={editData.chords || ''}
                                onChange={(e) => setEditData({ ...editData, chords: e.target.value })}
                                placeholder="Am, G, C, F..."
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Notes</Label>
                            <Textarea
                                value={editData.notes || ''}
                                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                                placeholder="Additional notes..."
                                className="mt-1 min-h-[60px]"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3 text-sm">
                        <div className="flex flex-wrap gap-x-6 gap-y-2">
                            {fragment.instrument && (
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">Instrument:</span>
                                    <span className="text-foreground font-medium">{fragment.instrument}</span>
                                </div>
                            )}
                            {fragment.tuning && (
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">Tuning:</span>
                                    <span className="text-foreground font-mono bg-background/50 px-2 py-0.5 rounded">{fragment.tuning}</span>
                                </div>
                            )}
                            {fragment.capo !== undefined && fragment.capo !== null && (
                                <div className="flex items-center gap-2">
                                    <span className="text-muted-foreground">Capo:</span>
                                    <span className="text-foreground">
                                        {fragment.capo === 0 ? 'None' : `Fret ${fragment.capo}`}
                                    </span>
                                </div>
                            )}
                        </div>
                        {fragment.chords && (
                            <div>
                                <span className="text-muted-foreground block mb-2">Chords:</span>
                                <div className="flex flex-wrap gap-2">
                                    {fragment.chords.split(/[,\s]+/).filter(Boolean).map((chord, i) => (
                                        <span
                                            key={i}
                                            className="px-3 py-1 bg-background/50 rounded-lg text-foreground font-mono text-sm border border-border/50"
                                        >
                                            {chord}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {fragment.notes && (
                            <p className="text-foreground mt-2 italic">{fragment.notes}</p>
                        )}
                    </div>
                );

            case 'tone':
                return (
                    <div className="flex items-center gap-4">
                        <div 
                            className="w-14 h-14 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `hsl(var(--${colorClass}) / 0.15)` }}
                        >
                            <span 
                                className="text-xl font-display font-semibold"
                                style={{ color: `hsl(var(--${colorClass}))` }}
                            >
                                {fragment.note}
                            </span>
                        </div>
                        <div className="text-sm">
                            <span className="text-muted-foreground">Reference pitch</span>
                            <span className="text-foreground block font-medium text-lg">
                                {fragment.frequency} Hz
                            </span>
                        </div>
                    </div>
                );

            case 'tuning':
                return (
                    <div className="space-y-3">
                        <div className="flex items-center gap-4">
                            {/* Tuning indicator circle */}
                            <div 
                                className={`w-14 h-14 rounded-full flex items-center justify-center ${
                                    fragment.inTune ? 'bg-success/15' : 'bg-muted'
                                }`}
                            >
                                <span 
                                    className={`text-lg font-display font-semibold ${
                                        fragment.inTune ? 'text-success' : 'text-foreground'
                                    }`}
                                >
                                    {fragment.detectedNote || '--'}
                                </span>
                            </div>
                            <div className="text-sm flex-1">
                                {fragment.tuningPreset && (
                                    <div className="text-xs text-muted-foreground mb-1">{fragment.tuningPreset}</div>
                                )}
                                {fragment.targetString && (
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-muted-foreground">String:</span>
                                        <span className="text-foreground font-medium">{fragment.targetString}</span>
                                    </div>
                                )}
                                {fragment.detectedFrequency && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-muted-foreground">Detected:</span>
                                        <span className="text-foreground font-mono">
                                            {fragment.detectedFrequency} Hz
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                        {/* Cents indicator */}
                        {fragment.cents !== null && fragment.cents !== undefined && (
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden relative">
                                    <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-success" />
                                    <div 
                                        className={`absolute top-0 bottom-0 w-2 rounded-full transition-all ${
                                            fragment.inTune ? 'bg-success' : Math.abs(fragment.cents) < 15 ? 'bg-warning' : 'bg-destructive'
                                        }`}
                                        style={{ 
                                            left: `${50 + (Math.max(-50, Math.min(50, fragment.cents)) / 50) * 45}%`,
                                            transform: 'translateX(-50%)'
                                        }}
                                    />
                                </div>
                                <span className={`text-xs font-mono w-16 text-right ${
                                    fragment.inTune ? 'text-success' : 'text-muted-foreground'
                                }`}>
                                    {fragment.cents > 0 ? '+' : ''}{fragment.cents} ¢
                                </span>
                            </div>
                        )}
                    </div>
                );

            case 'link':
                return editing ? (
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">URL</Label>
                            <Input
                                value={editData.url || ''}
                                onChange={(e) => setEditData({ ...editData, url: e.target.value })}
                                placeholder="https://..."
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Description</Label>
                            <Textarea
                                value={editData.description || ''}
                                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                                placeholder="What is this link about?"
                                className="mt-1 min-h-[60px]"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <a 
                            href={fragment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-primary hover:underline font-medium group"
                        >
                            <ExternalLink className="w-4 h-4 group-hover:scale-110 transition-transform" />
                            <span className="truncate">{fragment.url}</span>
                        </a>
                        {fragment.description && (
                            <p className="text-sm text-muted-foreground">{fragment.description}</p>
                        )}
                    </div>
                );

            case 'inspiration':
                return editing ? (
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">URL (Spotify, YouTube, SoundCloud)</Label>
                            <Input
                                value={editData.url || ''}
                                onChange={(e) => setEditData({ ...editData, url: e.target.value })}
                                placeholder="https://open.spotify.com/..."
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Note</Label>
                            <Textarea
                                value={editData.note || ''}
                                onChange={(e) => setEditData({ ...editData, note: e.target.value })}
                                placeholder="Why does this inspire you?"
                                className="mt-1 min-h-[60px]"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* Embed or link */}
                        {fragment.canEmbed && fragment.embedUrl ? (
                            <div className="rounded-lg overflow-hidden bg-muted/30">
                                <iframe
                                    src={fragment.embedUrl}
                                    width="100%"
                                    height={fragment.embedType === 'spotify' ? '80' : '200'}
                                    frameBorder="0"
                                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                    loading="lazy"
                                    className="block"
                                    title="Embedded content"
                                />
                            </div>
                        ) : (
                            <a 
                                href={fragment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-primary hover:underline font-medium group"
                            >
                                <Disc3 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                <span className="truncate">{fragment.url}</span>
                            </a>
                        )}
                        {fragment.note && (
                            <p className="text-sm text-muted-foreground italic">{fragment.note}</p>
                        )}
                    </div>
                );

            case 'drone':
                return editing ? (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Note</Label>
                                <Input
                                    value={editData.note || 'A2'}
                                    onChange={(e) => setEditData({ ...editData, note: e.target.value })}
                                    placeholder="A2"
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Frequency (Hz)</Label>
                                <Input
                                    type="number"
                                    value={editData.frequency || 110}
                                    onChange={(e) => setEditData({ ...editData, frequency: e.target.value })}
                                    className="mt-1"
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-4">
                        <div 
                            className="w-14 h-14 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `hsl(var(--${colorClass}) / 0.15)` }}
                        >
                            <span 
                                className="text-xl font-display font-semibold"
                                style={{ color: `hsl(var(--${colorClass}))` }}
                            >
                                {fragment.note}
                            </span>
                        </div>
                        <div className="text-sm">
                            <span className="text-muted-foreground">Drone reference</span>
                            <span className="text-foreground block font-medium text-lg">
                                {fragment.frequency} Hz
                            </span>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <Card 
            className={`overflow-hidden border-l-4 transition-all duration-normal hover:shadow-md ${
                hasCustomColor ? '' : config.bgClass
            }`}
            style={{
                borderLeftColor: `hsl(var(--${colorClass}))`,
                ...getCustomBgStyle()
            }}
        >
            <div className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `hsl(var(--${colorClass}) / 0.15)` }}
                        >
                            <Icon 
                                className="w-4.5 h-4.5" 
                                style={{ color: `hsl(var(--${colorClass}))` }} 
                            />
                        </div>
                        <div>
                            {editing ? (
                                <Input
                                    value={editData.label || ''}
                                    onChange={(e) => setEditData({ ...editData, label: e.target.value })}
                                    placeholder="Add a label..."
                                    className="h-7 text-sm"
                                />
                            ) : (
                                <span className="text-sm font-medium text-foreground">
                                    {fragment.label || config.label}
                                </span>
                            )}
                            <span className="text-xs text-muted-foreground block">
                                {formatDate(fragment.createdAt)}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {editing ? (
                            <>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleSave}
                                    className="h-8 w-8 text-success hover:text-success hover:scale-105 transition-transform"
                                >
                                    <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleCancelEdit}
                                    className="h-8 w-8 hover:scale-105 transition-transform"
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            </>
                        ) : (
                            <>
                                {/* Color picker */}
                                <ColorPicker 
                                    value={fragment.colorOverride} 
                                    onChange={handleColorChange}
                                />
                                {canEdit && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={handleEdit}
                                        className="h-8 w-8 text-muted-foreground hover:text-foreground hover:scale-105 transition-transform"
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setMoveOpen(true)}
                                    className="h-8 w-8 text-muted-foreground hover:text-foreground hover:scale-105 transition-transform"
                                    title="Move or copy"
                                >
                                    <ArrowRightLeft className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setDeleteOpen(true)}
                                    className="h-8 w-8 text-muted-foreground hover:text-destructive hover:scale-105 transition-transform"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="ml-12">
                    {renderContent()}
                </div>
            </div>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent className="max-w-sm animate-scale-in">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">Delete this fragment?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove the {fragment.type} fragment permanently.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Move/Copy Dialog */}
            <MoveFragmentDialog
                open={moveOpen}
                onOpenChange={setMoveOpen}
                fragmentId={fragment.id}
                currentSongId={songId}
                fragmentLabel={fragment.label}
            />
        </Card>
    );
}